import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  BarChart3,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Calculator,
  Percent,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';

interface TenantRevenueSummary {
  totalOperations: number;
  totalGrossAmount: number;
  totalPlatformFeesPaid: number;
  totalFeesCollected: number;
  totalNetRevenue: number;
  avgTransactionValue: number;
  period: { start: string; end: string };
  byOperationType?: Array<{
    operationType: string;
    count: number;
    grossAmount: number;
    feesCollected: number;
    platformFeesPaid: number;
    netRevenue: number;
  }>;
  comparison?: {
    previousPeriod: {
      totalOperations: number;
      totalGrossAmount: number;
      totalNetRevenue: number;
    };
    growthRate: {
      operations: number;
      volume: number;
      revenue: number;
    };
  };
}

interface TrendData {
  date?: string;
  month?: string;
  totalOperations: number;
  totalGrossAmount: number;
  totalNetRevenue: number;
}

const COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];

const OPERATION_LABELS: Record<string, string> = {
  PIX_RECEIVE: 'Pix Recebido',
  PIX_SEND: 'Pix Enviado',
  TRANSFER: 'Transferência',
  BOLETO: 'Boleto',
  CARD: 'Cartão',
  WITHDRAWAL: 'Saque',
  MAINTENANCE: 'Manutenção',
};

export function TenantRevenueDashboard() {
  const [period, setPeriod] = useState('30d');
  const [viewType, setViewType] = useState<'daily' | 'monthly'>('daily');

  const { startDate, endDate, startMonth, endMonth } = useMemo(() => {
    const now = new Date();
    let start: Date;
    
    switch (period) {
      case '7d':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      case '12m':
        start = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      startMonth: `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, '0')}`,
      endMonth: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`,
    };
  }, [period]);

  const { data: summary, isLoading: summaryLoading, refetch: refetchSummary } = useQuery<TenantRevenueSummary>({
    queryKey: ['tenant-revenue-summary', startDate, endDate],
    queryFn: async () => {
      const res = await api.get(ENDPOINTS.revenue.tenantSummary, {
        params: { startDate, endDate },
      });
      return res.data;
    },
  });

  const { data: trendData, isLoading: trendLoading } = useQuery<TrendData[]>({
    queryKey: ['tenant-revenue-trend', viewType, startDate, endDate, startMonth, endMonth],
    queryFn: async () => {
      if (viewType === 'daily') {
        const res = await api.get(ENDPOINTS.revenue.dailyTrend, {
          params: { startDate, endDate, scope: 'tenant' },
        });
        return res.data;
      } else {
        const res = await api.get(ENDPOINTS.revenue.monthlyTrend, {
          params: { startMonth, endMonth, scope: 'tenant' },
        });
        return res.data;
      }
    },
  });

  const marginPercentage = summary && summary.totalFeesCollected > 0
    ? ((summary.totalNetRevenue / summary.totalFeesCollected) * 100).toFixed(1)
    : '0';

  const operationChartData = summary?.byOperationType?.map((item) => ({
    name: OPERATION_LABELS[item.operationType] || item.operationType,
    value: item.netRevenue,
    count: item.count,
    feesCollected: item.feesCollected,
  })) || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Minha Receita</h1>
          <p className="text-surface-400 mt-1">
            Acompanhamento de receitas e margens do seu tenant
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-32"
          >
            <option value="7d">7 dias</option>
            <option value="30d">30 dias</option>
            <option value="90d">90 dias</option>
            <option value="12m">12 meses</option>
          </Select>
          <Select
            value={viewType}
            onChange={(e) => setViewType(e.target.value as 'daily' | 'monthly')}
            className="w-32"
          >
            <option value="daily">Diário</option>
            <option value="monthly">Mensal</option>
          </Select>
          <Button
            variant="ghost"
            onClick={() => refetchSummary()}
            disabled={summaryLoading}
          >
            <RefreshCw className={`w-4 h-4 ${summaryLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Volume Total</p>
              <p className="text-2xl font-bold text-white mt-1">
                {formatCurrency(summary?.totalGrossAmount || 0)}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                {summary?.totalOperations || 0} operações
              </p>
            </div>
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <DollarSign className="w-5 h-5 text-primary-400" />
            </div>
          </div>
          {summary?.comparison?.growthRate && (
            <div className="mt-2 flex items-center gap-1">
              {summary.comparison.growthRate.volume >= 0 ? (
                <ArrowUpRight className="w-3 h-3 text-green-400" />
              ) : (
                <ArrowDownRight className="w-3 h-3 text-red-400" />
              )}
              <span className={`text-xs ${summary.comparison.growthRate.volume >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {Math.abs(summary.comparison.growthRate.volume).toFixed(1)}% vs período anterior
              </span>
            </div>
          )}
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Taxas Cobradas</p>
              <p className="text-2xl font-bold text-white mt-1">
                {formatCurrency(summary?.totalFeesCollected || 0)}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                Taxas dos seus clientes
              </p>
            </div>
            <div className="p-2 bg-accent-500/20 rounded-lg">
              <Calculator className="w-5 h-5 text-accent-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Taxas Plataforma</p>
              <p className="text-2xl font-bold text-orange-400 mt-1">
                {formatCurrency(summary?.totalPlatformFeesPaid || 0)}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                Pago à plataforma
              </p>
            </div>
            <div className="p-2 bg-orange-500/20 rounded-lg">
              <TrendingDown className="w-5 h-5 text-orange-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Receita Líquida</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {formatCurrency(summary?.totalNetRevenue || 0)}
              </p>
              <div className="flex items-center gap-1 mt-1">
                <Percent className="w-3 h-3 text-surface-500" />
                <span className="text-xs text-surface-500">
                  {marginPercentage}% de margem
                </span>
              </div>
            </div>
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
          </div>
          {summary?.comparison?.growthRate && (
            <div className="mt-2 flex items-center gap-1">
              {summary.comparison.growthRate.revenue >= 0 ? (
                <ArrowUpRight className="w-3 h-3 text-green-400" />
              ) : (
                <ArrowDownRight className="w-3 h-3 text-red-400" />
              )}
              <span className={`text-xs ${summary.comparison.growthRate.revenue >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {Math.abs(summary.comparison.growthRate.revenue).toFixed(1)}% vs período anterior
              </span>
            </div>
          )}
        </Card>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trend Chart */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-primary-400" />
              Evolução da Receita
            </h3>
          </div>
          <div className="h-64">
            {trendLoading ? (
              <div className="h-full flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" />
              </div>
            ) : trendData && trendData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey={viewType === 'daily' ? 'date' : 'month'}
                    stroke="#9ca3af"
                    fontSize={12}
                    tickFormatter={(value) => {
                      if (viewType === 'daily') {
                        return new Date(value).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
                      }
                      return value;
                    }}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    fontSize={12}
                    tickFormatter={(value) => `R$ ${(value / 100).toFixed(0)}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value) => [formatCurrency(Number(value) || 0), 'Receita']}
                    labelFormatter={(label) => {
                      if (viewType === 'daily') {
                        return new Date(label).toLocaleDateString('pt-BR');
                      }
                      return label;
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="totalNetRevenue"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-surface-400">
                Sem dados para o período selecionado
              </div>
            )}
          </div>
        </Card>

        {/* Operation Type Distribution */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-accent-400" />
              Receita por Operação
            </h3>
          </div>
          <div className="h-64">
            {summaryLoading ? (
              <div className="h-full flex items-center justify-center">
                <RefreshCw className="w-6 h-6 text-surface-400 animate-spin" />
              </div>
            ) : operationChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={operationChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {operationChartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1f2937',
                      border: '1px solid #374151',
                      borderRadius: '8px',
                    }}
                    formatter={(value, name, props) => [
                      `${formatCurrency(Number(value) || 0)} (${props?.payload?.count || 0} ops)`,
                      String(name),
                    ]}
                  />
                  <Legend
                    verticalAlign="middle"
                    align="right"
                    layout="vertical"
                    wrapperStyle={{ fontSize: '12px' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-surface-400">
                Sem dados para o período selecionado
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Revenue Breakdown */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Calculator className="w-5 h-5 text-primary-400" />
            Breakdown por Operação
          </h3>
        </div>
        <div className="space-y-3">
          {summary?.byOperationType?.map((op, index) => (
            <div
              key={op.operationType}
              className="p-4 bg-surface-800/50 rounded-lg"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="font-medium text-white">
                    {OPERATION_LABELS[op.operationType] || op.operationType}
                  </span>
                  <span className="text-sm text-surface-500">
                    ({op.count} operações)
                  </span>
                </div>
                <span className="text-green-400 font-bold">
                  {formatCurrency(op.netRevenue)}
                </span>
              </div>
              <div className="grid grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-surface-500">Volume:</span>
                  <span className="text-white ml-2">{formatCurrency(op.grossAmount)}</span>
                </div>
                <div>
                  <span className="text-surface-500">Taxas Cobradas:</span>
                  <span className="text-white ml-2">{formatCurrency(op.feesCollected)}</span>
                </div>
                <div>
                  <span className="text-surface-500">Taxas Plataforma:</span>
                  <span className="text-orange-400 ml-2">-{formatCurrency(op.platformFeesPaid)}</span>
                </div>
                <div>
                  <span className="text-surface-500">Margem:</span>
                  <span className="text-green-400 ml-2">
                    {op.feesCollected > 0 
                      ? ((op.netRevenue / op.feesCollected) * 100).toFixed(1) 
                      : '0'}%
                  </span>
                </div>
              </div>
            </div>
          ))}
          {(!summary?.byOperationType || summary.byOperationType.length === 0) && (
            <div className="text-center py-8 text-surface-400">
              Sem dados para o período selecionado
            </div>
          )}
        </div>
      </Card>

      {/* Summary Card */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Resumo do Período</h3>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-blue-500" />
              <span className="text-surface-300">Volume Bruto</span>
            </div>
            <span className="text-white font-medium">
              {formatCurrency(summary?.totalGrossAmount || 0)}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-cyan-500" />
              <span className="text-surface-300">Taxas Cobradas dos Clientes</span>
            </div>
            <span className="text-cyan-400 font-medium">
              +{formatCurrency(summary?.totalFeesCollected || 0)}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-surface-300">(-) Taxas Pagas à Plataforma</span>
            </div>
            <span className="text-orange-400 font-medium">
              -{formatCurrency(summary?.totalPlatformFeesPaid || 0)}
            </span>
          </div>
          <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span className="text-green-300 font-medium">= Receita Líquida</span>
            </div>
            <span className="text-green-400 font-bold text-lg">
              {formatCurrency(summary?.totalNetRevenue || 0)}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default TenantRevenueDashboard;
